import { IS_PRODUCTION } from '@/lib/site'
import { useI18n } from '@/i18n/I18nProvider'
import type { Section, WrittenPage } from '@/content/sections'
import HeroSection from './sections/hero'
import CoverSection from './sections/cover'
import BannerSection from './sections/banner'
import SplitSection from './sections/split'
import ProseSection from './sections/prose'
import FeaturesSection from './sections/features'
import CardsSection from './sections/cards'
import CoversSection from './sections/covers'
import ListSection from './sections/list'
import QuotesSection from './sections/quotes'
import StatsSection from './sections/stats'
import ScaleSection from './sections/scale'
import PhotoSection from './sections/photo'
import FigureSection from './sections/figure'
import StackSection from './sections/stack'
import IssuesSection from './sections/issues'
import LogosSection from './sections/logos'
import CtaSection from './sections/cta'
import BenchmarkSection from './sections/benchmark'
import ToolSection from './sections/tool'
import FormSection from './sections/form'
import ApplySection from './sections/apply'

/**
 * Renders a written page from its sections.
 *
 * One component for all six, because the five pages Halil wrote share a dozen
 * section shapes between them and a component per page would be five hundred
 * lines of near-duplicate JSX that drifts apart the first time somebody edits one.
 * The copy decides the page; this decides the rhythm.
 *
 * The design language is the live digitopia.co's, measured: sections separated by
 * background colour rather than by bordered cards, one accent, pill buttons,
 * headlines that end in a full stop.
 */

/**
 * An unresolved claim from the copy document, shown beside the section it belongs
 * to — but only outside production.
 *
 * Rendering it in place rather than collecting it into a list is the point: a
 * reviewer reads "global leader in maturity assessment" and the question about it
 * in the same glance, which is when the answer is obvious. A list at the end of
 * the page is a list nobody opens.
 */
function CheckNote({ text }: { text?: string }) {
  const { t } = useI18n()
  if (!text || IS_PRODUCTION) return null
  return (
    <aside className="mt-8 rounded-md border-l-2 border-[#B45309] bg-[#FDF1E0] px-5 py-4 text-[0.95rem] leading-relaxed text-[#7A3E06]">
      <span className="mr-2 text-xs font-bold uppercase tracking-[0.12em]">
        {t.preview.checkLabel}
      </span>
      {text}
    </aside>
  )
}

/**
 * Which component draws this band.
 *
 * A switch, and not a `Record<Section['kind'], Renderer>` keyed by the union —
 * which is the tidier-looking version and the wrong one. TypeScript will not
 * correlate `RENDERERS[section.kind]` with `section`, so calling through a
 * record needs a cast, and that cast is precisely what would let a renderer be
 * wired to the wrong kind without a word from the compiler. The switch narrows
 * `section` for free and keeps the exhaustiveness check: add a `kind` to
 * `Section` and this function stops compiling, instead of that section quietly
 * rendering as an empty band.
 *
 * The bodies moved out on 2026-08-30. This file was 74KB, and 53 of the AI
 * editor's 264 commits touched it — so almost every layout request paid to read
 * all eighteen renderers, plus 537 lines of carousel machinery that only two of
 * them use. A hero request now reads `sections/hero.tsx`.
 */
function Body({ section }: { section: Section }) {
  switch (section.kind) {
    case 'hero':
      return <HeroSection section={section} />
    case 'cover':
      return <CoverSection section={section} />
    case 'banner':
      return <BannerSection section={section} />
    case 'split':
      return <SplitSection section={section} />
    case 'prose':
      return <ProseSection section={section} />
    case 'features':
      return <FeaturesSection section={section} />
    case 'cards':
      return <CardsSection section={section} />
    case 'list':
      return <ListSection section={section} />
    case 'quotes':
      return <QuotesSection section={section} />
    case 'stats':
      return <StatsSection section={section} />
    case 'scale':
      return <ScaleSection section={section} />
    case 'photo':
      return <PhotoSection section={section} />
    case 'figure':
      return <FigureSection section={section} />
    case 'stack':
      return <StackSection section={section} />
    case 'issues':
      return <IssuesSection section={section} />
    case 'logos':
      return <LogosSection section={section} />
    case 'covers':
      return <CoversSection section={section} />
    case 'cta':
      return <CtaSection section={section} />
    case 'benchmark':
      return <BenchmarkSection section={section} />
    case 'tool':
      return <ToolSection section={section} />
    case 'form':
      return <FormSection section={section} />
    case 'apply':
      return <ApplySection section={section} />
  }
}

/** Sections alternate white and light-grey; `cta` with `tone: 'ink'` goes dark. */
function background(section: Section, index: number): string {
  // The dark opener. `relative isolate` is what keeps its backdrop photograph
  // and scrim — both `-z-10` — inside this section instead of behind the whole
  // document, where `Layout`'s gradient ribbon lives at the same depth.
  if (section.kind === 'cover') return 'relative isolate overflow-hidden bg-ink text-ink-foreground'
  if (section.kind === 'cta' && section.tone === 'ink') return 'bg-ink text-ink-foreground'
  if (section.kind === 'cta' && section.tone === 'band') return 'bg-band'
  // Both draw their own card on the page, so the page stays white under them —
  // a grey band behind a grey card is a card with a halo, and a grey band
  // behind the gradient one dulls it.
  if (section.kind === 'banner') return ''
  if (section.kind === 'cta' && section.tone === 'card') return ''
  // The Hall of Fame card is `bg-band` itself, so it is the same argument a
  // third time — and here it is not only a halo: a grey card on a grey band has
  // no edge at all, and the card's arc and the portrait standing on its bottom
  // border are what tell the reader where it ends. Left to the alternation this
  // would be white or grey depending on how many sections happen to sit above
  // it, which is not something the copy should be able to break by accident.
  // The same for the minimal two-up: those cards are `bg-band` too, and with no
  // arc and no standing figure the card's edge is the only thing separating it
  // from the page, so the page under them has to stay white.
  if (
    section.kind === 'quotes'
    && (section.variant === 'fame'
      || section.variant === 'minimal'
      || section.variant === 'minimal-carousel')
  )
    return ''
  // A paired argument-and-photograph never takes a turn in the alternation. A
  // page built from five of them wants five white sections with the picture
  // changing sides, not a striped page fighting the alternation for attention.
  if (section.kind === 'split') return ''
  // The `rows` features variant draws its own grey card per item, so the page
  // under it stays white — same argument as the cards above: a grey card on a
  // grey band has no edge, and the card edge is the only thing separating one
  // value from the next.
  if (section.kind === 'features' && section.variant === 'rows') return ''
  // A centred standfirst belongs to the opener above it rather than to the
  // bands below: it is the second half of the page's first impression, and a
  // grey band directly under the dark cover makes three stripes in a row.
  if (section.kind === 'prose' && section.align === 'center') return ''
  if (section.kind === 'stats' || section.kind === 'scale') return 'bg-band'
  // A photograph is always on white and never counts towards the alternation.
  // On a grey band it reads as a framed illustration of the section above it,
  // which is the opposite of what it is for — and letting it take a turn in the
  // alternation would put two grey sections next to each other.
  if (section.kind === 'photo') return ''
  // Same for a diagram, and here it is legibility rather than rhythm: the wheels
  // are transparent exports with dark navy lettering, so whatever colour the band
  // is becomes the paper they are printed on. White is the paper they were drawn
  // for.
  if (section.kind === 'figure') return ''
  // The trust bar sits directly under the hero, which is always white; a grey
  // band one section down would read as the page already having ended once.
  if (section.kind === 'stack') return ''
  // The newsletter archive draws its own hairline rules between the issues, and
  // those rules are the only structure it has. On a grey band they read as the
  // band's own internal divisions rather than as a list, and the list is the
  // page. White wherever it sits, so its position in the copy cannot change
  // whether it looks like a list.
  if (section.kind === 'issues') return ''
  // A tool band is the one grey section the top-of-page ribbon still reaches, and
  // an opaque #f5f5f5 cut it off in a straight line across the page — asked on
  // 2026-08-26 to let the ribbon carry on over it instead. The band is therefore
  // the same colour at 70%, which is enough for the gradient behind it to show
  // through as a tint and still read as a grey band beside the white sections.
  // Raising the ribbon itself was the alternative and it is not available: it is
  // one layer for the whole document at `-z-10` (see `app/Layout.tsx`), so
  // lifting it here would put it over every hero photograph on the site.
  if (section.kind === 'tool') return 'bg-[hsl(var(--band)/0.7)]'
  return index % 2 === 0 ? '' : 'bg-band'
}

/**
 * A photograph gets less air than an argument. The standard rhythm is 96px of
 * padding; a photo band at that spacing floats in the middle of nowhere, because
 * the image itself is already the pause.
 */
function padding(section: Section): string {
  // The cover is the first screen and nothing is above it: it gets the deepest
  // air on the site, so the lettering sits in the middle of the band rather
  // than filling it.
  if (section.kind === 'cover') return 'py-28 sm:py-40'
  // Both are a pause rather than an argument — a photograph is the pause itself,
  // and the trust bar is a single glance on the way to the first real argument.
  // Giving it the standard 96px would make "here is what we integrate with" read
  // as equal in weight to the paragraphs around it, when its job is to be quick.
  if (section.kind === 'photo' || section.kind === 'stack') return 'py-6 sm:py-10'
  // A diagram is read rather than glanced at, so it gets more air than a
  // photograph does — but less than an argument, because the section above it is
  // the argument and the two belong together.
  if (section.kind === 'figure') return 'py-10 sm:py-14'
  // A tool always sits directly under its page's hero, and the first thing in it
  // is now the Start button. The standard 96px above it put that button most of
  // a screen below the headline it belongs to; half that keeps it inside the
  // hero's own block of the page, which is where it was asked to be.
  //
  // The 96px below it went the same way on 2026-08-26 ("gri kısım bu kadar
  // büyük olmasın"): the intro is a button and two cards, and a band that ends
  // half a screen after its last card reads as a section still waiting for
  // something. The type inside grew in the same round, so the band gets shorter
  // by losing air rather than by losing anything to read.
  if (section.kind === 'tool') return 'pt-6 pb-10 sm:pt-8 sm:pb-12'
  // Opts out of the top half of the standard rhythm, for a centred standfirst
  // whose own top padding stacks with the hero's bottom padding above it into
  // a gap wider than either section intended.
  if (section.kind === 'prose' && section.tightTop) return 'pt-0 pb-12 sm:pb-16'
  // The standard rhythm, tightened one step on 2026-08-31 from `py-16 sm:py-24`.
  // Two neighbouring bands each contribute their own padding, so 96px a side was
  // 192px of empty page between an argument and the one that answers it — asked
  // for on a screenshot of Solutions where "Strategy Advisory", "How we work"
  // and "Proof" read as three unrelated pages. 64px a side (128px between) is
  // still an unmistakable break; going below this starts to look cramped rather
  // than considered.
  return 'py-12 sm:py-16'
}

export default function ContentPage({ page }: { page: WrittenPage }) {
  return (
    <>
      {page.sections.map((section, i) => (
        <section key={`${section.kind}-${i}`} id={section.anchor} className={background(section, i)}>
          <div className={`site-container ${padding(section)}`}>
            <Body section={section} />
            <CheckNote text={section.check} />
          </div>
        </section>
      ))}
    </>
  )
}
